import { useEffect, useState } from 'react';
import { api, type Asset, type Card, type Turn } from '../lib/api';

/**
 * The still that decides whether anything else gets watched, chosen.
 *
 * Thomas: "grab any scene and screenshot, annotate with text… or AI edited
 * based on theme to create a HOOK… or a completely different uploaded
 * asset." Three ways in, one picture out, and the render's own stays the
 * fallback — clear the choice and it is back.
 */
export function ThumbnailPanel({ card, rundown, selectedLine, assets, onChanged }: {
  card: Card;
  rundown: Turn[];
  /** The scene the playhead is on — the frame "Use this frame" grabs. */
  selectedLine: number | null;
  assets: Asset[];
  onChanged: (card: Card) => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [chosen, setChosen] = useState(false);
  const [headline, setHeadline] = useState(card.seo_title || card.title);
  const [kicker, setKicker] = useState('');
  const [tag, setTag] = useState('');
  const [stamp, setStamp] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [assetId, setAssetId] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.thumbnail(card.id)
      .then((r) => { if (live) { setUrl(r.download.url); setChosen(Boolean(r.chosen)); } })
      .catch(() => { if (live) { setUrl(null); setChosen(false); } });
    return () => { live = false; };
  }, [card.id, card.thumbnail_key, card.master_job_id]);

  async function go(label: string, fn: () => Promise<{ card: Card; download?: { url: string } } | Card>) {
    setBusy(label); setError(null);
    try {
      const r = await fn();
      const next = 'card' in r ? r.card : r;
      onChanged(next);
      if ('download' in r && r.download) { setUrl(r.download.url); setChosen(true); }
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  }

  const line = selectedLine ?? 0;
  const pictures = assets.filter((a) => a.kind === 'image' && a.status === 'ready');

  return (
    <div className="thumbstudio" data-tool="thumbnail">
      <div className="row between">
        <span className="lbl-mono">Thumbnail{chosen ? ' · yours' : url ? " · the render's" : ''}</span>
        {chosen && (
          <button className="btn tiny" disabled={busy !== null}
                  onClick={() => void go('clear', () => api.clearThumbnail(card.id))}>
            back to the render&apos;s
          </button>
        )}
      </div>
      {url ? (
        <figure className="thumbpreview">
          <img src={url} alt="The thumbnail that will be published with this cut" />
          <figcaption>{chosen ? 'Published as the thumbnail — your choice' : 'Published as the thumbnail — the render made it'}</figcaption>
        </figure>
      ) : (
        <p className="sub-dim">No thumbnail yet — render the card, or choose one below.</p>
      )}
      {error && <div className="err inline">{error}</div>}

      <div className="thumb-way">
        <b>This frame, under a headline</b>
        <span className="sub-dim">Scene {line + 1}{rundown[line] ? ` — ${rundown[line]!.speaker}` : ''}. Move the playhead to pick another.</span>
        <input value={headline} maxLength={120} placeholder="The hook, in a few words — wrap a word in **stars** for the accent"
               title="Wrap a word in **double stars** and the headline is set in caps with an outline, that word in the accent colour — the two-colour hook"
               onChange={(e) => setHeadline(e.target.value)} />
        <div className="row" style={{ gap: 6 }}>
          <input value={kicker} maxLength={40} placeholder="Small line above it (optional)" style={{ flex: 1 }}
                 onChange={(e) => setKicker(e.target.value)} />
          <select value={tag} onChange={(e) => setTag(e.target.value)} title="A pill in the accent, top right">
            <option value="">no tag</option>
            {['LIVE', 'DEBATE', 'EXCLUSIVE', 'NEW', 'REPLAY', 'PART 2'].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <label className="sub-dim" title="Today's date, small, bottom right — a daily show's cards read as today's">
            <input type="checkbox" checked={stamp} onChange={(e) => setStamp(e.target.checked)} /> date
          </label>
          <button className="btn pri" disabled={busy !== null || !rundown.length}
                  onClick={() => void go('frame', () => api.setThumbnail(card.id, {
                    kind: 'frame', line, headline: headline.trim() || undefined, kicker: kicker.trim() || undefined,
                    ...(tag ? { tag } : {}), ...(stamp ? { stamp: true } : {}), rundown,
                  }))}>
            {busy === 'frame' ? 'Drawing…' : 'Use this frame'}
          </button>
          <button className="btn" disabled={busy !== null || !rundown.length}
                  title="The cast's faces, huge and tight, as vertical slices under the headline — the face strip"
                  onClick={() => void go('faces', () => api.setThumbnail(card.id, {
                    kind: 'frame', line, template: 'faces', headline: headline.trim() || undefined, kicker: kicker.trim() || undefined,
                    ...(tag ? { tag } : {}), ...(stamp ? { stamp: true } : {}), rundown,
                  }))}>
            {busy === 'faces' ? 'Drawing…' : 'Faces'}
          </button>
          <button className="btn" disabled={busy !== null || !rundown.length}
                  title="The show's set as the ground — saturated, the headline over it. Needs a set on the card (Brand kit → defaults, or the Mix panel)."
                  onClick={() => void go('set', () => api.setThumbnail(card.id, {
                    kind: 'frame', line, template: 'set', headline: headline.trim() || undefined, kicker: kicker.trim() || undefined,
                    ...(tag ? { tag } : {}), ...(stamp ? { stamp: true } : {}), rundown,
                  }))}>
            {busy === 'set' ? 'Drawing…' : 'On the set'}
          </button>
          <button className="btn" disabled={busy !== null || !rundown.length}
                  title="Two pictures on a diagonal — this frame and the set — the small line as a vertical tag, the speaker's face in a round inset over the join"
                  onClick={() => void go('split', () => api.setThumbnail(card.id, {
                    kind: 'frame', line, template: 'split', headline: headline.trim() || undefined, kicker: kicker.trim() || undefined,
                    ...(tag ? { tag } : {}), ...(stamp ? { stamp: true } : {}), rundown,
                  }))}>
            {busy === 'split' ? 'Drawing…' : 'Split'}
          </button>
          <button className="btn" disabled={busy !== null || !rundown.length}
                  title="The cast's faces (or this frame) with up to three of this episode's pictures over them as tilted, bordered cards — this scene's graphic pictures when it has them, else the first three gathered"
                  onClick={() => void go('cards', () => api.setThumbnail(card.id, {
                    kind: 'frame', line, template: 'cards', headline: headline.trim() || undefined, kicker: kicker.trim() || undefined,
                    ...(tag ? { tag } : {}), ...(stamp ? { stamp: true } : {}), rundown,
                  }))}>
            {busy === 'cards' ? 'Drawing…' : 'Cards'}
          </button>
        </div>
      </div>

      <div className="thumb-way">
        <b>Made from the theme</b>
        <span className="sub-dim">The picture model, from the theme, the subject and the headline. The picture carries no lettering of its own; the headline above, the tag, the date and the show's logo are set on it afterwards, as on a frame.</span>
        <input value={prompt} maxLength={500} placeholder="Anything to add — e.g. a wilted tomato plant in harsh noon light"
               onChange={(e) => setPrompt(e.target.value)} />
        <div className="row" style={{ gap: 6 }}>
          <button className="btn" disabled={busy !== null}
                  onClick={() => void go('made', () => api.setThumbnail(card.id, {
                    kind: 'made', prompt: prompt.trim() || undefined, headline: headline.trim() || undefined,
                    ...(tag ? { tag } : {}), ...(stamp ? { stamp: true } : {}),
                  }))}>
            {busy === 'made' ? 'Making…' : 'Make one'}
          </button>
          <button className="btn" disabled={busy !== null || !rundown.length}
                  title="Start from the selected scene's frame and let the model rework it"
                  onClick={() => void go('edit', () => api.setThumbnail(card.id, {
                    kind: 'made', prompt: prompt.trim() || undefined, headline: headline.trim() || undefined, fromLine: line, rundown,
                    ...(tag ? { tag } : {}), ...(stamp ? { stamp: true } : {}),
                  }))}>
            {busy === 'edit' ? 'Reworking…' : 'Rework this frame'}
          </button>
          <button className="btn" disabled={busy !== null}
                  title="The cast cut out of their portraits and set large over a dramatic scene the model makes from the theme (and anything you add above). Needs faces on the cast."
                  onClick={() => void go('cutout', () => api.setThumbnail(card.id, {
                    kind: 'made', cutout: true, prompt: prompt.trim() || undefined, headline: headline.trim() || undefined,
                    ...(tag ? { tag } : {}), ...(stamp ? { stamp: true } : {}),
                  }))}>
            {busy === 'cutout' ? 'Cutting out…' : 'Cut-outs over a scene'}
          </button>
        </div>
      </div>

      <div className="thumb-way">
        <b>A picture from the library</b>
        <div className="row" style={{ gap: 6 }}>
          <select value={assetId} onChange={(e) => setAssetId(e.target.value)} style={{ flex: 1, minWidth: 0 }}>
            <option value="">choose a picture…</option>
            {pictures.map((a) => <option key={a.id} value={a.id}>{a.name?.trim() || a.storage_key.split('/').pop()}</option>)}
          </select>
          <button className="btn" disabled={busy !== null || !assetId}
                  onClick={() => void go('asset', () => api.setThumbnail(card.id, { kind: 'asset', assetId }))}>
            {busy === 'asset' ? 'Setting…' : 'Use it'}
          </button>
        </div>
      </div>
    </div>
  );
}
